import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarDays, Check, ClipboardList, Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { BrandAmbassadorPicker } from '../../components/BrandAmbassadorPicker';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { StorePicker } from '../../components/StorePicker';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import { campaignBelongsToWorkspace } from '../../lib/workspace';
import type {
  Assignment,
  Campaign,
  CampaignShift,
  Organization,
  ShiftSchedule,
  TalentProfile,
  Venue,
} from '../../types/domain';

interface AssignmentDraft {
  campaign: string;
  target: string;
  brand_ambassador: string;
  hired_by: string;
}

interface AssignmentFilters {
  search: string;
  client: string;
  agency: string;
  campaign: string;
  venue: string;
  status: Assignment['status'] | '';
  dateFrom: string;
  dateTo: string;
}

interface TalentPopoverPosition {
  bottom?: number;
  left: number;
  resultsMaxHeight: number;
  top?: number;
  width: number;
}

interface AssignmentTargetOption {
  detail: string;
  kind: 'Individual shift' | 'Schedule';
  label: string;
  value: string;
}

const emptyDraft: AssignmentDraft = {
  campaign: '',
  target: '',
  brand_ambassador: '',
  hired_by: '',
};

const emptyAssignmentFilters: AssignmentFilters = {
  search: '',
  client: '',
  agency: '',
  campaign: '',
  venue: '',
  status: '',
  dateFrom: '',
  dateTo: '',
};

const assignmentPageSize = 25;

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Karachi',
});

export function AssignmentsPage() {
  const { selectedWorkspace } = useAuth();
  return <AssignmentsWorkspacePage key={selectedWorkspace?.id ?? 'personal'} />;
}

function AssignmentsWorkspacePage() {
  const {
    accessToken,
    capabilities,
    isPlatformAdmin,
    profile,
    selectedWorkspace,
  } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTalentReference = searchParams.get('talent')?.trim() ?? '';
  const canManageAssignments = capabilities.includes('assignments:manage');
  const [creating, setCreating] = useState(Boolean(initialTalentReference));
  const [draft, setDraft] = useState<AssignmentDraft>(emptyDraft);
  const [selectedTalent, setSelectedTalent] = useState<TalentProfile | null>(null);
  const [talentSearch, setTalentSearch] = useState(initialTalentReference);
  const [debouncedTalentSearch, setDebouncedTalentSearch] = useState(initialTalentReference);
  const [talentPage, setTalentPage] = useState(1);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [activeScheduleIndex, setActiveScheduleIndex] = useState(0);
  const [schedulePopoverPosition, setSchedulePopoverPosition] = useState<TalentPopoverPosition | null>(null);
  const [routeTalentHandled, setRouteTalentHandled] = useState(!initialTalentReference);
  const [cancelTarget, setCancelTarget] = useState<Assignment | null>(null);
  const [assignmentFilters, setAssignmentFilters] = useState<AssignmentFilters>(emptyAssignmentFilters);
  const [debouncedAssignmentSearch, setDebouncedAssignmentSearch] = useState('');
  const [assignmentPage, setAssignmentPage] = useState(1);
  const scheduleComboboxRef = useRef<HTMLDivElement>(null);
  const schedulePopoverRef = useRef<HTMLDivElement>(null);
  const scheduleSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedTalentSearch(talentSearch.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [talentSearch]);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedAssignmentSearch(assignmentFilters.search.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [assignmentFilters.search]);
  const assignmentParams = new URLSearchParams({
    page: String(assignmentPage),
    page_size: String(assignmentPageSize),
    ordering: 'created_at',
  });
  if (selectedWorkspace?.organizationId) {
    assignmentParams.set('workspace', selectedWorkspace.organizationId);
  }
  if (assignmentFilters.status) {
    assignmentParams.set('status', assignmentFilters.status);
  }
  if (assignmentFilters.client) {
    assignmentParams.set('client', assignmentFilters.client);
  }
  if (assignmentFilters.agency) {
    assignmentParams.set('agency', assignmentFilters.agency);
  }
  if (assignmentFilters.campaign) {
    assignmentParams.set('campaign', assignmentFilters.campaign);
  }
  if (assignmentFilters.venue) {
    assignmentParams.set('venue', assignmentFilters.venue);
  }
  if (assignmentFilters.dateFrom) {
    assignmentParams.set('date_from', assignmentFilters.dateFrom);
  }
  if (assignmentFilters.dateTo) {
    assignmentParams.set('date_to', assignmentFilters.dateTo);
  }
  if (/^ASN-\d+$/i.test(debouncedAssignmentSearch)) {
    assignmentParams.set('reference', debouncedAssignmentSearch);
  } else if (/^BA-\d+$/i.test(debouncedAssignmentSearch)) {
    assignmentParams.set('brand_ambassador_reference', debouncedAssignmentSearch);
  } else if (debouncedAssignmentSearch) {
    assignmentParams.set('search', debouncedAssignmentSearch);
  }
  const assignments = useApiCollection<Assignment>(
    'assignments',
    `/assignments/?${assignmentParams.toString()}`,
    accessToken,
  );
  const campaigns = useApiCollection<Campaign>(
    'assignment-campaigns',
    '/campaigns/?page_size=100&ordering=-starts_on',
    accessToken,
  );
  const schedules = useApiCollection<ShiftSchedule>(
    'assignment-schedules',
    `/shift-schedules/?campaign=${draft.campaign}&page_size=100&ordering=starts_on`,
    accessToken,
    canManageAssignments && Boolean(draft.campaign),
  );
  const individualShifts = useApiCollection<CampaignShift>(
    'assignment-individual-shifts',
    `/shifts/?campaign=${draft.campaign}&manual=true&page_size=100&ordering=starts_at`,
    accessToken,
    canManageAssignments && Boolean(draft.campaign),
  );
  const talentParams = new URLSearchParams({
    assignment_eligible: 'true',
    ordering: 'verification_rank,availability_rank,display_name',
    page: String(talentPage),
    page_size: '20',
  });
  if (debouncedTalentSearch) {
    talentParams.set(/^BA-\d+$/i.test(debouncedTalentSearch) ? 'reference' : 'search', debouncedTalentSearch);
  }
  const talent = useApiCollection<TalentProfile>(
    'assignment-talent',
    `/talent/?${talentParams.toString()}`,
    accessToken,
    canManageAssignments && creating,
  );
  const organizations = useApiCollection<Organization>(
    'assignment-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
    canManageAssignments,
  );
  const venues = useApiCollection<Venue>(
    'assignment-venues',
    '/venues/?page_size=100&ordering=name',
    accessToken,
  );
  const rows = assignments.data?.results ?? [];
  const assignmentCount = assignments.data?.count ?? 0;
  const assignmentPageCount = Math.max(1, Math.ceil(assignmentCount / assignmentPageSize));
  const firstVisibleAssignmentPage = Math.max(
    1,
    Math.min(assignmentPage - 2, assignmentPageCount - 4),
  );
  const visibleAssignmentPages = Array.from(
    { length: Math.min(5, assignmentPageCount) },
    (_, index) => firstVisibleAssignmentPage + index,
  );
  const assignmentCampaignOptions = useMemo(
    () => (campaigns.data?.results ?? []).filter((campaign) => (
      campaignBelongsToWorkspace(campaign, selectedWorkspace)
    )),
    [campaigns.data?.results, selectedWorkspace],
  );
  const campaignOptions = useMemo(
    () => assignmentCampaignOptions.filter((campaign) => (
      campaign.status !== 'completed' && campaign.status !== 'cancelled'
    )),
    [assignmentCampaignOptions],
  );
  const clientOptions = useMemo(
    () => [...new Map(assignmentCampaignOptions.map((campaign) => [
      campaign.client,
      { id: campaign.client, name: campaign.client_name },
    ])).values()],
    [assignmentCampaignOptions],
  );
  const agencyOptions = useMemo(
    () => [...new Map(assignmentCampaignOptions.flatMap((campaign) => (
      campaign.operating_agency
        ? [[campaign.operating_agency, {
            id: campaign.operating_agency,
            name: campaign.operating_agency_name ?? 'Agency',
          }] as const]
        : []
    ))).values()],
    [assignmentCampaignOptions],
  );
  const scheduleOptions = (schedules.data?.results ?? []).filter(
    (schedule) => schedule.campaign === draft.campaign,
  );
  const individualShiftOptions = (individualShifts.data?.results ?? []).filter(
    (shift) => shift.campaign === draft.campaign,
  );
  const assignmentTargetOptions = useMemo<AssignmentTargetOption[]>(() => [
    ...scheduleOptions.map((schedule) => ({
      detail: `${schedule.location_name} · ${dateFormatter.format(new Date(`${schedule.starts_on}T00:00:00+05:00`))}–${dateFormatter.format(new Date(`${schedule.ends_on}T00:00:00+05:00`))} · ${schedule.occurrence_count} shifts`,
      kind: 'Schedule' as const,
      label: schedule.name,
      value: `schedule:${schedule.id}`,
    })),
    ...individualShiftOptions.map((shift) => ({
      detail: `${shift.location_name} · ${dateTimeFormatter.format(new Date(shift.starts_at))}`,
      kind: 'Individual shift' as const,
      label: 'One-off shift',
      value: `shift:${shift.id}`,
    })),
  ], [individualShiftOptions, scheduleOptions]);
  const filteredAssignmentTargetOptions = useMemo(() => {
    const search = scheduleSearch.trim().toLocaleLowerCase();
    if (!search) return assignmentTargetOptions;
    return assignmentTargetOptions.filter((option) => (
      `${option.label} ${option.detail} ${option.kind}`.toLocaleLowerCase().includes(search)
    ));
  }, [assignmentTargetOptions, scheduleSearch]);
  const selectedAssignmentTarget = assignmentTargetOptions.find((option) => option.value === draft.target) ?? null;
  const talentOptions = useMemo(
    () => (talent.data?.results ?? [])
      .filter((person) => person.is_assignment_eligible)
      .sort((left, right) => (
        Number(right.verification_status === 'verified') - Number(left.verification_status === 'verified')
        || Number(right.is_available) - Number(left.is_available)
        || left.display_name.localeCompare(right.display_name)
        || left.reference.localeCompare(right.reference)
      )),
    [talent.data?.results],
  );
  const talentPageCount = Math.max(1, Math.ceil((talent.data?.count ?? 0) / 20));

  const positionSchedulePopover = useCallback(() => {
    const anchor = scheduleComboboxRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportMargin = 12;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
    const spaceAbove = rect.top - viewportMargin;
    const opensAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(140, (opensAbove ? spaceAbove : spaceBelow) - gap);
    const popoverHeight = Math.min(330, availableHeight);
    setSchedulePopoverPosition({
      ...(opensAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      resultsMaxHeight: popoverHeight,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!scheduleOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !scheduleComboboxRef.current?.contains(target)
        && !schedulePopoverRef.current?.contains(target)
      ) {
        setScheduleOpen(false);
      }
    };
    const frame = window.requestAnimationFrame(positionSchedulePopover);
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('resize', positionSchedulePopover);
    window.addEventListener('scroll', positionSchedulePopover, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('resize', positionSchedulePopover);
      window.removeEventListener('scroll', positionSchedulePopover, true);
    };
  }, [positionSchedulePopover, scheduleOpen]);

  const hiringOptions = useCallback((campaignId: string) => {
    const campaign = campaignOptions.find((item) => item.id === campaignId);
    if (!campaign) return [];
    const participants = new Set([
      campaign.owner_organization,
      campaign.client,
      campaign.operating_agency,
    ].filter((id): id is string => Boolean(id)));
    return (organizations.data?.results ?? []).filter((organization) => (
      participants.has(organization.id)
      && (isPlatformAdmin || organization.id === selectedWorkspace?.organizationId)
    ));
  }, [campaignOptions, isPlatformAdmin, organizations.data?.results, selectedWorkspace?.organizationId]);

  const availableHiringOrganizations = hiringOptions(draft.campaign);

  useEffect(() => {
    if (routeTalentHandled || talent.isLoading || campaigns.isLoading) return;
    const timeout = window.setTimeout(() => {
      const person = talentOptions.find(
        (candidate) => candidate.reference.toLowerCase() === initialTalentReference.toLowerCase(),
      );
      if (person) {
        const campaign = campaignOptions.length === 1 ? (campaignOptions.at(0) ?? null) : null;
        const possibleHiringOrganizations = hiringOptions(campaign?.id ?? '');
        setSelectedTalent(person);
        setDraft({
          ...emptyDraft,
          brand_ambassador: person.user_id,
          campaign: campaign?.id ?? '',
          hired_by: possibleHiringOrganizations.length === 1
            ? (possibleHiringOrganizations.at(0)?.id ?? '')
            : '',
        });
      }
      setRouteTalentHandled(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('talent');
      setSearchParams(nextParams, { replace: true });
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [
    campaignOptions,
    campaigns.isLoading,
    initialTalentReference,
    hiringOptions,
    routeTalentHandled,
    searchParams,
    setSearchParams,
    talent.isLoading,
    talentOptions,
  ]);

  const createOffer = useMutation({
    mutationFn: () => {
      const [targetType, targetId] = draft.target.split(':', 2);
      return apiRequest<Assignment>('/assignments/', {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body: JSON.stringify({
          campaign: draft.campaign,
          brand_ambassador: draft.brand_ambassador,
          hired_by: draft.hired_by,
          [targetType === 'schedule' ? 'schedule_id' : 'shift_id']: targetId,
        }),
      });
    },
    onSuccess: async () => {
      setCreating(false);
      setDraft(emptyDraft);
      setScheduleSearch('');
      setScheduleOpen(false);
      setSelectedTalent(null);
      setTalentSearch('');
      await queryClient.invalidateQueries({ queryKey: ['collection', 'assignments'] });
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'cancelled' | 'completed' }) => (
      apiRequest<Assignment>(`/assignments/${id}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    ),
    onSuccess: async () => {
      setCancelTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'assignments'] });
    },
  });

  const respondToOffer = useMutation({
    mutationFn: ({ id, response }: { id: string; response: 'accept' | 'decline' }) => (
      apiRequest<Assignment>(`/assignments/${id}/${response}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
      })
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collection', 'assignments'] });
    },
  });

  function beginCreate() {
    createOffer.reset();
    const campaign = campaignOptions.length === 1 ? (campaignOptions.at(0) ?? null) : null;
    const possibleHiringOrganizations = hiringOptions(campaign?.id ?? '');
    setDraft({
      ...emptyDraft,
      campaign: campaign?.id ?? '',
      hired_by: possibleHiringOrganizations.length === 1
        ? (possibleHiringOrganizations.at(0)?.id ?? '')
        : '',
    });
    setSelectedTalent(null);
    setTalentSearch('');
    setDebouncedTalentSearch('');
    setTalentPage(1);
    setScheduleSearch('');
    setScheduleOpen(false);
    setActiveScheduleIndex(0);
    setCreating(true);
  }

  function closeCreate() {
    if (createOffer.isPending) return;
    setCreating(false);
    setDraft(emptyDraft);
    setSelectedTalent(null);
    setTalentSearch('');
    setDebouncedTalentSearch('');
    setTalentPage(1);
    setScheduleSearch('');
    setScheduleOpen(false);
    setActiveScheduleIndex(0);
    createOffer.reset();
  }

  function chooseTalent(person: TalentProfile) {
    setSelectedTalent(person);
    setDraft((current) => ({ ...current, brand_ambassador: person.user_id }));
  }

  function clearSelectedTalent() {
    setSelectedTalent(null);
    setDraft((current) => ({ ...current, brand_ambassador: '' }));
    setTalentSearch('');
    setDebouncedTalentSearch('');
    setTalentPage(1);
  }

  function chooseAssignmentTarget(target: AssignmentTargetOption) {
    setDraft((current) => ({ ...current, target: target.value }));
    setScheduleSearch('');
    setScheduleOpen(false);
    setActiveScheduleIndex(0);
  }

  function clearAssignmentTarget() {
    setDraft((current) => ({ ...current, target: '' }));
    setScheduleSearch('');
    setScheduleOpen(true);
    setActiveScheduleIndex(0);
    window.requestAnimationFrame(() => scheduleSearchRef.current?.focus());
  }

  function handleScheduleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setScheduleOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setScheduleOpen(true);
      if (filteredAssignmentTargetOptions.length === 0) return;
      setActiveScheduleIndex((current) => (
        event.key === 'ArrowDown'
          ? (current + 1) % filteredAssignmentTargetOptions.length
          : (current - 1 + filteredAssignmentTargetOptions.length) % filteredAssignmentTargetOptions.length
      ));
      return;
    }
    if (event.key === 'Enter' && scheduleOpen && filteredAssignmentTargetOptions[activeScheduleIndex]) {
      event.preventDefault();
      chooseAssignmentTarget(filteredAssignmentTargetOptions[activeScheduleIndex]);
    }
  }

  function selectCampaign(campaignId: string) {
    const possibleHiringOrganizations = hiringOptions(campaignId);
    setDraft((current) => ({
      ...current,
      campaign: campaignId,
      target: '',
      hired_by: possibleHiringOrganizations.length === 1
        ? (possibleHiringOrganizations.at(0)?.id ?? '')
        : '',
    }));
    setScheduleSearch('');
    setScheduleOpen(false);
    setActiveScheduleIndex(0);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!draft.target) return;
    createOffer.mutate();
  }

  function updateAssignmentFilter<Key extends keyof AssignmentFilters>(
    key: Key,
    value: AssignmentFilters[Key],
  ) {
    setAssignmentFilters((current) => ({ ...current, [key]: value }));
    setAssignmentPage(1);
  }

  function resetAssignmentFilters() {
    setAssignmentFilters(emptyAssignmentFilters);
    setDebouncedAssignmentSearch('');
    setAssignmentPage(1);
  }

  function requestCancellation(assignment: Assignment) {
    updateStatus.reset();
    setCancelTarget(assignment);
  }

  function closeCancellation() {
    if (updateStatus.isPending) return;
    setCancelTarget(null);
    updateStatus.reset();
  }

  const canManageRow = (assignment: Assignment) => canManageAssignments && (
    isPlatformAdmin || assignment.hired_by === selectedWorkspace?.organizationId
  );

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Assignments</h1>
          <p>Create assignment offers and manage brand ambassadors across campaign schedules.</p>
        </div>
        {!canManageAssignments ? (
          <span className="record-count"><ClipboardList size={16} /> {assignmentCount}</span>
        ) : null}
      </header>

      <section className="content-card attendance-filter-card assignment-filter-card">
        <div className="card-heading">
          <div><h2>Filter assignments</h2><p>Results and totals use the same authorised workspace scope.</p></div>
          <button className="text-button" onClick={resetAssignmentFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid assignment-filter-grid">
          <label className="attendance-search-filter">
            BA, campaign, store or reference
            <span><Search size={16} /><input aria-label="Search assignments" onChange={(event) => updateAssignmentFilter('search', event.target.value)} placeholder="Search by name or reference" type="search" value={assignmentFilters.search} /></span>
          </label>
          <label>
            Client
            <select aria-label="Assignment client" onChange={(event) => updateAssignmentFilter('client', event.target.value)} value={assignmentFilters.client}>
              <option value="">All clients</option>
              {clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
          <label>
            Agency
            <select aria-label="Assignment agency" onChange={(event) => updateAssignmentFilter('agency', event.target.value)} value={assignmentFilters.agency}>
              <option value="">All agencies</option>
              {agencyOptions.map((agency) => <option key={agency.id} value={agency.id}>{agency.name}</option>)}
            </select>
          </label>
          <label>
            Campaign
            <select aria-label="Assignment campaign" onChange={(event) => updateAssignmentFilter('campaign', event.target.value)} value={assignmentFilters.campaign}>
              <option value="">All campaigns</option>
              {assignmentCampaignOptions.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
            </select>
          </label>
          <label>
            Store
            <StorePicker
              allOptionLabel="All stores"
              ariaLabel="Assignment store"
              onChange={(value) => updateAssignmentFilter('venue', value)}
              options={(venues.data?.results ?? []).map((venue) => ({
                id: venue.id,
                name: venue.name,
                address: venue.address,
                city: venue.city,
                chainName: venue.chain_name,
                storeCode: venue.store_code,
              }))}
              value={assignmentFilters.venue}
            />
          </label>
          <label>
            Status
            <select aria-label="Assignment status" onChange={(event) => updateAssignmentFilter('status', event.target.value as Assignment['status'] | '')} value={assignmentFilters.status}>
              <option value="">All statuses</option>
              <option value="offered">Offered</option>
              <option value="accepted">Accepted</option>
              <option value="declined">Declined</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </label>
          <label>Date from<input max={assignmentFilters.dateTo || undefined} onChange={(event) => updateAssignmentFilter('dateFrom', event.target.value)} type="date" value={assignmentFilters.dateFrom} /></label>
          <label>Date to<input min={assignmentFilters.dateFrom || undefined} onChange={(event) => updateAssignmentFilter('dateTo', event.target.value)} type="date" value={assignmentFilters.dateTo} /></label>
        </div>
      </section>

      {canManageAssignments ? (
        <div className="assignment-list-toolbar assignment-create-toolbar">
          <button className="button primary" onClick={beginCreate} type="button"><Plus size={17} /> Add assignment</button>
        </div>
      ) : null}

      {creating ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeCreate(); }}
          role="presentation"
        >
        <section aria-labelledby="assignment-offer-dialog-title" aria-modal="true" className="confirmation-dialog assignment-offer-dialog" role="dialog">
          <button aria-label="Close assignment offer form" className="icon-button confirmation-close" disabled={createOffer.isPending} onClick={closeCreate} type="button"><X size={19} /></button>
          <div className="card-heading assignment-offer-dialog-heading">
            <div><h2 id="assignment-offer-dialog-title">Create assignment offer</h2><p>Select an eligible ambassador and one schedule. The offer covers every dated shift in that schedule.</p></div>
          </div>
          <form className="management-form" onSubmit={submit}>
            <div className="form-grid">
              <label>
                Campaign
                <select required value={draft.campaign} onChange={(event) => selectCampaign(event.target.value)}>
                  <option value="">Select campaign</option>
                  {campaignOptions.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name} · {campaign.client_name}</option>)}
                </select>
              </label>
              <div className="form-field assignment-target-selector-field">
                <span>Shift schedule</span>
                {selectedAssignmentTarget ? (
                  <div className="talent-selection assignment-target-selection">
                    <span>
                      <strong>{selectedAssignmentTarget.label}</strong>
                      <small>{selectedAssignmentTarget.detail}</small>
                    </span>
                    <button className="text-button" disabled={createOffer.isPending} onClick={clearAssignmentTarget} type="button">Change</button>
                  </div>
                ) : (
                  <div className="talent-combobox" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget) && !schedulePopoverRef.current?.contains(event.relatedTarget)) setScheduleOpen(false); }} ref={scheduleComboboxRef}>
                    <label className="talent-search-field">
                      <Search aria-hidden="true" size={17} />
                      <input
                        aria-activedescendant={scheduleOpen && filteredAssignmentTargetOptions[activeScheduleIndex] ? `assignment-schedule-${filteredAssignmentTargetOptions[activeScheduleIndex].value}` : undefined}
                        aria-autocomplete="list"
                        aria-controls="assignment-schedule-results"
                        aria-expanded={scheduleOpen}
                        aria-label="Shift schedule"
                        aria-required="true"
                        disabled={!draft.campaign || createOffer.isPending}
                        onChange={(event) => {
                          setScheduleSearch(event.target.value);
                          setActiveScheduleIndex(0);
                          setScheduleOpen(true);
                        }}
                        onFocus={() => setScheduleOpen(true)}
                        onKeyDown={handleScheduleKeyDown}
                        placeholder={draft.campaign ? 'Search schedule or store' : 'Select a campaign first'}
                        ref={scheduleSearchRef}
                        role="combobox"
                        value={scheduleSearch}
                      />
                    </label>
                    {scheduleOpen && schedulePopoverPosition ? createPortal(
                      <div
                        className="talent-combobox-popover assignment-target-popover"
                        ref={schedulePopoverRef}
                        style={{
                          ...(schedulePopoverPosition.top === undefined ? {} : { top: schedulePopoverPosition.top }),
                          ...(schedulePopoverPosition.bottom === undefined ? {} : { bottom: schedulePopoverPosition.bottom }),
                          left: schedulePopoverPosition.left,
                          width: schedulePopoverPosition.width,
                        }}
                      >
                        <div className="talent-search-results" id="assignment-schedule-results" role="listbox" style={{ maxHeight: schedulePopoverPosition.resultsMaxHeight }}>
                          {schedules.isLoading || individualShifts.isLoading ? <p><InlineSpinner /> Loading schedules…</p> : null}
                          {!schedules.isLoading && !individualShifts.isLoading && filteredAssignmentTargetOptions.length === 0 ? <p>No schedules match this search.</p> : null}
                          {filteredAssignmentTargetOptions.map((target, index) => (
                            <button aria-selected={index === activeScheduleIndex} className={index === activeScheduleIndex ? 'active' : ''} id={`assignment-schedule-${target.value}`} key={target.value} onClick={() => chooseAssignmentTarget(target)} onMouseEnter={() => setActiveScheduleIndex(index)} role="option" type="button">
                              <span>
                                <strong>{target.label}</strong>
                                <small>{target.detail}</small>
                              </span>
                              <small className="assignment-target-kind">{target.kind}</small>
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body,
                    ) : null}
                  </div>
                )}
              </div>
              <div className="form-field talent-selector-field">
                <span>Brand ambassador</span>
                <BrandAmbassadorPicker
                  error={talent.isError}
                  idPrefix="assignment-talent"
                  loading={talent.isLoading}
                  onClear={clearSelectedTalent}
                  onPageChange={setTalentPage}
                  onSearchChange={(value) => { setTalentSearch(value); setTalentPage(1); }}
                  onSelect={chooseTalent}
                  options={talentOptions}
                  page={talentPage}
                  pageCount={talentPageCount}
                  search={talentSearch}
                  selected={selectedTalent}
                />
              </div>
              <label>
                Hiring organisation
                <select disabled={!draft.campaign} required value={draft.hired_by} onChange={(event) => setDraft({ ...draft, hired_by: event.target.value })}>
                  <option value="">Select organisation</option>
                  {availableHiringOrganizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                </select>
              </label>
            </div>
            {createOffer.isError ? <p className="form-error" role="alert">{getApiErrorMessage(createOffer.error)}</p> : null}
            <div className="form-actions">
              <button className="button secondary" disabled={createOffer.isPending} onClick={closeCreate} type="button">Cancel</button>
              <button className="button primary" disabled={createOffer.isPending || !draft.brand_ambassador || !draft.target} type="submit">{createOffer.isPending ? <InlineSpinner /> : <Plus size={16} />} {createOffer.isPending ? 'Creating…' : 'Create offer'}</button>
            </div>
          </form>
        </section>
        </div>
      ) : null}

      {updateStatus.isError && !cancelTarget ? <p className="form-error page-error" role="alert">{getApiErrorMessage(updateStatus.error)}</p> : null}
      {respondToOffer.isError ? <p className="form-error page-error" role="alert">{getApiErrorMessage(respondToOffer.error)}</p> : null}
      <DataPanel loading={assignments.isLoading} error={assignments.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Brand ambassador</th><th>Campaign</th><th>Shift</th><th>Store</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((assignment) => (
                  <tr key={assignment.id}>
                    <td><strong>{assignment.brand_ambassador_name}</strong><small>{assignment.brand_ambassador_reference} · {assignment.reference} · Hired by {assignment.hired_by_name}</small></td>
                    <td>{assignment.campaign_name}</td>
                    <td><span className="with-icon"><CalendarDays size={14} /> {assignment.schedule ? `${assignment.schedule.name} · ${dateFormatter.format(new Date(`${assignment.schedule.starts_on}T00:00:00+05:00`))}–${dateFormatter.format(new Date(`${assignment.schedule.ends_on}T00:00:00+05:00`))}` : dateTimeFormatter.format(new Date(assignment.shift.starts_at))}</span></td>
                    <td>{assignment.shift.location.name}<small>{assignment.shift.location.city}</small></td>
                    <td><StatusPill value={assignment.status} /></td>
                    <td>
                      <div className="table-actions">
                        {assignment.status === 'offered' && assignment.brand_ambassador === profile?.id ? (
                          <>
                            <button className="text-button" disabled={respondToOffer.isPending} onClick={() => respondToOffer.mutate({ id: assignment.id, response: 'accept' })} type="button"><Check size={14} /> Accept</button>
                            <button className="text-button danger-text" disabled={respondToOffer.isPending} onClick={() => respondToOffer.mutate({ id: assignment.id, response: 'decline' })} type="button"><Ban size={14} /> Decline</button>
                          </>
                        ) : null}
                        {canManageRow(assignment) && assignment.status === 'accepted' ? (
                          <button className="text-button" disabled={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: assignment.id, status: 'completed' })} type="button"><Check size={14} /> Complete</button>
                        ) : null}
                        {canManageRow(assignment) && (assignment.status === 'offered' || assignment.status === 'accepted') ? (
                          <button className="text-button danger-text" disabled={updateStatus.isPending} onClick={() => requestCancellation(assignment)} type="button"><Ban size={14} /> Cancel</button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination assignment-pagination">
            <span>Page {assignmentPage} of {assignmentPageCount} · {assignmentCount} matching {assignmentCount === 1 ? 'assignment' : 'assignments'}</span>
            <div>
              <button className="button secondary" disabled={!assignments.data?.previous} onClick={() => setAssignmentPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
              {visibleAssignmentPages.map((pageNumber) => (
                <button
                  aria-current={pageNumber === assignmentPage ? 'page' : undefined}
                  className={`button secondary page-number${pageNumber === assignmentPage ? ' active' : ''}`}
                  key={pageNumber}
                  onClick={() => setAssignmentPage(pageNumber)}
                  type="button"
                >
                  {pageNumber}
                </button>
              ))}
              <button className="button secondary" disabled={!assignments.data?.next} onClick={() => setAssignmentPage((current) => Math.min(assignmentPageCount, current + 1))} type="button">Next</button>
            </div>
          </div>
        </section>
      </DataPanel>

      {cancelTarget ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeCancellation(); }} role="presentation">
          <section aria-labelledby="cancel-assignment-dialog-title" aria-modal="true" className="confirmation-dialog" role="dialog">
            <button aria-label="Close cancellation confirmation" className="icon-button confirmation-close" disabled={updateStatus.isPending} onClick={closeCancellation} type="button"><X size={18} /></button>
            <div className="confirmation-icon"><Ban size={22} /></div>
            <h2 id="cancel-assignment-dialog-title">Cancel assignment {cancelTarget.reference}?</h2>
            <p><strong>{cancelTarget.brand_ambassador_name}</strong> will lose the assignment offer for <strong>{cancelTarget.campaign_name}</strong>.</p>
            <p className="confirmation-note">{cancelTarget.schedule ? `${cancelTarget.schedule.name} · ` : ''}{cancelTarget.shift.location.name}, {cancelTarget.shift.location.city} · {dateTimeFormatter.format(new Date(cancelTarget.shift.starts_at))}</p>
            <p className="confirmation-note">The cancelled record will remain in assignment history and cannot be restored from this screen.</p>
            {updateStatus.isError ? <p className="form-error" role="alert">{getApiErrorMessage(updateStatus.error)}</p> : null}
            <div className="confirmation-actions">
              <button autoFocus className="button secondary" disabled={updateStatus.isPending} onClick={closeCancellation} type="button">Keep assignment</button>
              <button className="button danger" disabled={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: cancelTarget.id, status: 'cancelled' })} type="button">{updateStatus.isPending ? <InlineSpinner /> : <Ban size={16} />}{updateStatus.isPending ? 'Cancelling…' : 'Confirm cancellation'}</button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
